import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { ArrowLeft, Printer, Eye, Edit, RefreshCw, AlertTriangle } from 'lucide-react'
import { orderService, adminService } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import Currency from '../components/Currency'

// Error boundary to catch silent render crashes
class OrderDetailsErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }
  componentDidCatch(error, info) {
    console.error('[AdminOrderDetails] Render crash:', error, info)
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <AlertTriangle className="mx-auto h-12 w-12 text-red-400 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">خطأ في عرض الطلب</h2>
              <p className="text-red-600 text-sm font-mono mb-4 bg-red-50 p-3 rounded text-left">
                {this.state.error?.message || String(this.state.error)}
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload() }}
                  className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  إعادة تحميل
                </button>
                <button
                  onClick={() => window.location.href = '/admin'}
                  className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  العودة للإدارة
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

const AdminOrderDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  useEffect(() => {
    console.log('[AdminOrderDetails] Mounted - Order ID:', id)
  }, [id])

  // Fetch order details
  const { data: orderResponse, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-order', id],
    queryFn: async () => {
      console.log('[AdminOrderDetails] Fetching order:', id)
      const result = await adminService.getOrderDetails(id)
      console.log('[AdminOrderDetails] API response:', result)
      return result
    },
    enabled: !!id,
    retry: 2,
    retryDelay: 1000,
  })

  useEffect(() => {
    if (error) {
      console.error('[AdminOrderDetails] Query error:', error)
    }
  }, [error])

  useEffect(() => {
    if (orderResponse) {
      console.log('[AdminOrderDetails] Order data received, success:', orderResponse?.success)
    }
  }, [orderResponse])

  // Update order status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ orderId, status, notes }) => 
      orderService.updateOrderStatus(orderId, status, notes),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-order', id])
      queryClient.invalidateQueries(['admin-orders'])
      toast.success('Order status updated successfully')
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update order status')
    }
  })

  if (isLoading || isFetching) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">جاري تحميل تفاصيل الطلب #{id}...</p>
        </div>
      </div>
    )
  }

  if (error || !orderResponse?.success) {
    const errMsg = error?.response?.data?.message || error?.message || 
                   orderResponse?.message || 'فشل في تحميل الطلب'
    const errCode = error?.response?.data?.code || error?.response?.status || ''
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-red-400 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">خطأ في تحميل الطلب</h2>
            <p className="text-gray-600 mb-2">{errMsg}</p>
            {errCode && (
              <p className="text-xs text-gray-400 mb-6 font-mono">
                Code: {errCode} | Order ID: {id}
              </p>
            )}
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => refetch()}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                إعادة المحاولة
              </button>
              <button
                onClick={() => navigate('/admin')}
                className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                العودة للإدارة
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const order = orderResponse.order

  // Safely parse address data
  const safeParseJson = (value) => {
    if (!value) return null
    if (typeof value === 'object') return value
    try { return JSON.parse(value) } catch (e) {
      console.warn('[AdminOrderDetails] Failed to parse address JSON:', value)
      return null
    }
  }

  const billingAddress = safeParseJson(order.billing_address)
  const shippingAddress = safeParseJson(order.shipping_address)

  const handleStatusUpdate = (newStatus) => {
    updateStatusMutation.mutate({
      orderId: order.id,
      status: newStatus,
      notes: ''
    })
  }

  const handlePrintInvoice = () => {
    window.open(`/admin/orders/${id}/invoice`, '_blank')
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin')}
                className="inline-flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                العودة للطلبات
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  الطلب #{order.order_number}
                </h1>
                <p className="text-gray-600">
                  تم الإنشاء في {new Date(order.created_at).toLocaleDateString('ar-EG')}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handlePrintInvoice}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <Printer className="w-4 h-4 mr-2" />
                طباعة الفاتورة
              </button>
              <Link
                to={`/order-status/${order.order_number}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <Eye className="w-4 h-4 mr-2" />
                عرض العميل
              </Link>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Status */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">حالة الطلب</h3>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm text-gray-600">الحالة الحالية:</span>
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      order.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                      order.status === 'shipped' ? 'bg-purple-100 text-purple-800' :
                      order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                      order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {order.status_display || order.status}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Update Status:</label>
                  <select
                    value={order.status}
                    onChange={(e) => handleStatusUpdate(e.target.value)}
                    disabled={updateStatusMutation.isLoading}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="refunded">Refunded</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Order Items */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Items</h3>
              <div className="space-y-4">
                {order.items && order.items.length > 0 ? (
                  order.items.map((item, index) => (
                    <div key={index} className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{item.product_name || item.name}</h4>
                        
                        {/* Display attributes */}
                        {item.selected_attributes && (() => {
                          const attrs = typeof item.selected_attributes === 'string'
                            ? (() => { try { return JSON.parse(item.selected_attributes) } catch(e) { return null } })()
                            : item.selected_attributes
                          return attrs && Object.keys(attrs).length > 0 ? (
                            <div className="text-sm text-blue-600 mt-1">
                              Attributes: {Object.entries(attrs).map(([k, v]) => `${k}: ${v}`).join(', ')}
                            </div>
                          ) : null
                        })()}
                        
                        {(item.attribute_name || item.attribute_variation_name) && (
                          <div className="text-sm text-blue-600 mt-1">
                            {item.attribute_name && `${item.attribute_name}`}
                            {item.attribute_variation_name && `: ${item.attribute_variation_name}`}
                            {item.attribute_variation_value && ` (${item.attribute_variation_value})`}
                          </div>
                        )}
                        
                        <div className="text-sm text-gray-500 mt-1">
                          ID: #{item.product_id} | 
                          {(item.product_sku || item.sku) && ` SKU: ${item.product_sku || item.sku} | `}
                          <Link 
                            to={`/products/${item.product_id}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:text-primary-700 ml-1"
                          >
                            عرض المنتج ←
                          </Link>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-sm text-gray-600">الكمية: {item.quantity}</div>
                        <div className="font-medium"><Currency amount={item.price} /></div>
                        <div className="text-sm text-gray-600">
                          الإجمالي: <Currency amount={item.price * item.quantity} />
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-gray-500 text-center py-8">لا توجد عناصر</div>
                )}
              </div>
            </div>

            {/* Customer Information */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">معلومات العميل</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">تفاصيل التواصل</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">الاسم:</span>
                      <span>
                        {order.customer_name || 
                         (order.first_name && order.last_name ? `${order.first_name} ${order.last_name}` : null) ||
                         (billingAddress ? `${billingAddress.first_name || ''} ${billingAddress.last_name || ''}`.trim() : null) ||
                         'N/A'
                        }
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">البريد الإلكتروني:</span>
                      <span>{order.customer_email || order.email || billingAddress?.email || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">الهاتف:</span>
                      <span>{order.customer_phone || order.phone || billingAddress?.phone || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Type:</span>
                      <span>{order.customer_type || (order.user_id ? 'User' : 'Guest')}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Shipping Address</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Address:</span>
                      <span className="text-right">
                        {shippingAddress?.address || 
                         billingAddress?.address || 
                         (typeof order.shipping_address === 'object' ? order.shipping_address?.address : order.shipping_address) ||
                         'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">City:</span>
                      <span>{order.city_name || order.city || shippingAddress?.city || billingAddress?.city || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Governorate:</span>
                      <span>{order.governorate_name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Country:</span>
                      <span>{order.country_name || shippingAddress?.country || billingAddress?.country || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Order Summary */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Order Number:</span>
                  <span className="font-medium">#{order.order_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span>{new Date(order.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Method:</span>
                  <span className="capitalize">{order.payment_method_name || order.payment_method || 'N/A'}</span>
                </div>
                {order.subtotal && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span><Currency amount={order.subtotal} /></span>
                  </div>
                )}
                {order.tax_amount && parseFloat(order.tax_amount) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax:</span>
                    <span><Currency amount={order.tax_amount} /></span>
                  </div>
                )}
                {order.shipping_amount && parseFloat(order.shipping_amount) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Shipping:</span>
                    <span><Currency amount={order.shipping_amount} /></span>
                  </div>
                )}
                <div className="flex justify-between border-t pt-3 mt-3">
                  <span className="text-gray-900 font-semibold">Total Amount:</span>
                  <span className="font-bold text-lg"><Currency amount={order.total_amount} /></span>
                </div>
              </div>
            </div>

            {/* Admin Actions */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Admin Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={handlePrintInvoice}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Invoice
                </button>
                
                <Link
                  to={`/order-status/${order.order_number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Customer View
                </Link>

                {/* Add more admin actions here as needed */}
              </div>
            </div>

            {/* Notes Section */}
            {order.notes && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Notes</h3>
                <p className="text-sm text-gray-600">{order.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function AdminOrderDetailsPage(props) {
  return (
    <OrderDetailsErrorBoundary>
      <AdminOrderDetails {...props} />
    </OrderDetailsErrorBoundary>
  )
}
